<script setup lang="ts">
/**
 * MiniCart - Slide-out cart drawer
 * ================================
 * Shows cart contents in a side panel.
 */
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useCartStore } from '@/stores/cart'

const { t } = useI18n()
const cartStore = useCartStore()

const isOpen = computed(() => cartStore.isMiniCartOpen)
const items = computed(() => cartStore.items)
const itemCount = computed(() => cartStore.itemCount)
const subtotal = computed(() => cartStore.subtotal)
const isEmpty = computed(() => cartStore.isEmpty)
const isLoading = computed(() => cartStore.isLoading)

function close() {
  cartStore.closeMiniCart()
}

async function updateQuantity(itemId: number, quantity: number) {
  await cartStore.updateQuantity(itemId, quantity)
}

async function removeItem(itemId: number) {
  await cartStore.removeItem(itemId)
}

function formatPrice(price: number): string {
  return price.toFixed(2)
}
</script>

<template>
  <!-- Backdrop -->
  <Transition name="fade">
    <div
      v-if="isOpen"
      class="fixed inset-0 bg-black/50 z-40"
      @click="close"
    ></div>
  </Transition>

  <!-- Drawer -->
  <Transition name="slide-right">
    <div
      v-if="isOpen"
      class="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-xl z-50 flex flex-col"
    >
      <!-- Header -->
      <div class="flex items-center justify-between p-4 border-b">
        <h2 class="font-heading text-xl text-charcoal">
          {{ $t('cart.your_cart') }}
          <span v-if="itemCount > 0" class="text-gray-500 font-normal text-base">
            ({{ $t('cart.items_count', { count: itemCount }) }})
          </span>
        </h2>
        <button
          @click="close"
          class="p-2 text-gray-400 hover:text-charcoal transition-colors"
          :aria-label="$t('common.close')"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Loading -->
      <div v-if="isLoading" class="flex-1 flex items-center justify-center">
        <div class="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full"></div>
      </div>

      <!-- Empty State -->
      <div v-else-if="isEmpty" class="flex-1 flex flex-col items-center justify-center p-8 text-center">
        <svg class="w-20 h-20 text-gray-200 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
        </svg>
        <h3 class="font-heading text-lg text-charcoal mb-2">{{ $t('cart.empty_title') }}</h3>
        <p class="text-gray-500 mb-6">{{ $t('minicart.discover_jewelry') }}</p>
        <RouterLink
          to="/products"
          @click="close"
          class="btn-primary"
        >
          {{ $t('cart.explore_products') }}
        </RouterLink>
      </div>

      <!-- Cart Items -->
      <div v-else class="flex-1 overflow-y-auto p-4">
        <ul class="space-y-4">
          <li
            v-for="item in items"
            :key="item.id"
            class="flex gap-4 pb-4 border-b border-gray-100 last:border-0"
          >
            <!-- Product Image -->
            <RouterLink
              :to="{ name: 'product-detail', params: { id: item.product.id } }"
              @click="close"
              class="flex-shrink-0"
            >
              <img
                :src="item.product.images?.[0]?.image_url || 'https://via.placeholder.com/80'"
                :alt="item.product.name"
                class="w-20 h-20 object-cover rounded-lg"
              />
            </RouterLink>

            <!-- Product Info -->
            <div class="flex-1 min-w-0">
              <RouterLink
                :to="{ name: 'product-detail', params: { id: item.product.id } }"
                @click="close"
                class="font-medium text-charcoal hover:text-primary-500 line-clamp-1"
              >
                {{ item.product.name }}
              </RouterLink>

              <p class="text-sm text-gray-500 mt-1">
                {{ formatPrice(item.price) }} EUR
              </p>

              <!-- Quantity Controls -->
              <div class="flex items-center gap-2 mt-2">
                <div class="flex items-center border border-gray-200 rounded">
                  <button
                    @click="updateQuantity(item.id, item.quantity - 1)"
                    class="px-2 py-1 text-gray-500 hover:text-charcoal"
                    :disabled="isLoading"
                  >
                    -
                  </button>
                  <span class="px-2 py-1 text-sm font-medium min-w-[2rem] text-center">
                    {{ item.quantity }}
                  </span>
                  <button
                    @click="updateQuantity(item.id, item.quantity + 1)"
                    class="px-2 py-1 text-gray-500 hover:text-charcoal"
                    :disabled="isLoading || item.quantity >= item.product.stock_quantity"
                  >
                    +
                  </button>
                </div>

                <button
                  @click="removeItem(item.id)"
                  class="text-gray-400 hover:text-red-500 transition-colors ml-auto"
                  :disabled="isLoading"
                  :aria-label="$t('common.remove')"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>

            <!-- Item Subtotal -->
            <div class="text-right">
              <p class="font-semibold text-charcoal">
                {{ formatPrice(item.subtotal) }} EUR
              </p>
            </div>
          </li>
        </ul>
      </div>

      <!-- Footer -->
      <div v-if="!isEmpty && !isLoading" class="border-t p-4 bg-gray-50">
        <!-- Subtotal -->
        <div class="flex justify-between items-center mb-4">
          <span class="text-gray-600">{{ $t('cart.subtotal') }}</span>
          <span class="font-heading text-xl text-charcoal">{{ formatPrice(subtotal) }} EUR</span>
        </div>

        <p class="text-xs text-gray-500 mb-4">
          {{ $t('minicart.shipping_calculated') }}
        </p>

        <!-- Buttons -->
        <div class="space-y-2">
          <RouterLink
            to="/checkout"
            @click="close"
            class="btn-primary w-full text-center block"
          >
            {{ $t('cart.checkout') }}
          </RouterLink>
          <RouterLink
            to="/cart"
            @click="close"
            class="btn-secondary w-full text-center block"
          >
            {{ $t('minicart.view_full_cart') }}
          </RouterLink>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
/* Fade transition for backdrop */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Slide transition for drawer */
.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.3s ease;
}
.slide-right-enter-from,
.slide-right-leave-to {
  transform: translateX(100%);
}
</style>
